import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { TraceProductInfo } from "../graphql/queries";
import { ProductInfo, TraceProduct, EventProductInstance, ProductDetailsMap } from "../types";

export function useProductDetails() {
    const client = useApolloClient();
    const [productDetails, setProductDetails] = useState<ProductDetailsMap>({});
    const [loadingEventIds, setLoadingEventIds] = useState<Set<string>>(new Set());
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const pendingEventIds = useRef<Set<string>>(new Set());

    const fetchProductDetails = useCallback(async (eventProdInstances: EventProductInstance[]) => {
        const newInstances = eventProdInstances.filter(
            item => !fetchedEventIds.current.has(item.eventId) && !pendingEventIds.current.has(item.eventId)
        );

        if (newInstances.length === 0) return;

        const newEventIds = newInstances.map(item => item.eventId);
        newInstances.forEach(item => pendingEventIds.current.add(item.eventId));
        setLoadingEventIds(prev => new Set([...prev, ...newEventIds]));

        const maxRetries = 2;
        let attempt = 0;
        let success = false;

        while (attempt < maxRetries && !success) {
            try {
                const { data } = await client.query({
                    query: TraceProductInfo,
                    variables: {
                        input: {
                            eventProdInstancesInput: newInstances,
                            isAuth: false
                        }
                    },
                    fetchPolicy: "network-only",
                });

                const traceProducts: TraceProduct[] = data?.traceProductInfo?.traceProducts || [];

                const newProductDetails: ProductDetailsMap = {};
                traceProducts.forEach((product) => {
                    const productInfoList = product.productInfoList || [];
                    newProductDetails[product.eventId] = productInfoList.length > 0 ? productInfoList : null;
                    fetchedEventIds.current.add(product.eventId);
                });

                newInstances.forEach(item => {
                    if (!newProductDetails.hasOwnProperty(item.eventId)) {
                        newProductDetails[item.eventId] = null;
                        fetchedEventIds.current.add(item.eventId);
                    }
                });

                setProductDetails(prev => ({
                    ...prev,
                    ...newProductDetails
                }));

                success = true;
            } catch (error) {
                attempt++;
                if (attempt >= maxRetries) {
                    newInstances.forEach(item => {
                        fetchedEventIds.current.add(item.eventId);
                    });
                }
            }
        }

        newInstances.forEach(item => pendingEventIds.current.delete(item.eventId));
        setLoadingEventIds(prev => {
            const newSet = new Set(prev);
            newEventIds.forEach(id => newSet.delete(id));
            return newSet;
        });
    }, [client]);

    const getProductInfo = useCallback((eventId: string): ProductInfo[] | null | undefined => {
        return productDetails[eventId];
    }, [productDetails]);

    const hasProductInfo = useCallback((eventId: string): boolean => {
        return fetchedEventIds.current.has(eventId);
    }, []);

    const isProductLoading = useCallback((eventId: string): boolean => {
        return loadingEventIds.has(eventId);
    }, [loadingEventIds]);

    return {
        productDetails,
        loadingEventIds,
        isProductLoading,
        fetchProductDetails,
        getProductInfo,
        hasProductInfo
    };
}
